import AdminNavbar from "@/components/AdminNavbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, MessageSquare, BarChart, Bot } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { ApplicationsGraph } from "@/components/admin/ApplicationsGraph";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { sendManualChatbotReport } from "@/components/membership/utils/emailService";

const AdminDashboard = () => {
  const { data: membershipStats } = useQuery({
    queryKey: ["membership-stats"],
    queryFn: async () => {
      const { count: totalCount } = await supabase
        .from("membership_applications")
        .select("*", { count: "exact", head: true });

      const { count: pendingCount } = await supabase
        .from("membership_applications")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");

      return {
        total: totalCount || 0,
        pending: pendingCount || 0
      };
    }
  });

  const { data: messageStats } = useQuery({
    queryKey: ["message-stats"],
    queryFn: async () => {
      const { count } = await supabase
        .from("contact_submissions")
        .select("*", { count: "exact", head: true });

      return count || 0;
    }
  });

  const { data: articleStats } = useQuery({
    queryKey: ["article-stats"],
    queryFn: async () => {
      const { count: publishedCount } = await supabase
        .from("articles")
        .select("*", { count: "exact", head: true })
        .eq("status", "published");

      const { count: draftCount } = await supabase
        .from("articles")
        .select("*", { count: "exact", head: true })
        .eq("status", "draft");

      return {
        published: publishedCount || 0,
        drafts: draftCount || 0
      };
    }
  });

  const { data: applicationTimeline } = useQuery({
    queryKey: ["application-timeline"],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from("membership_applications")
        .select("created_at")
        .gte("created_at", thirtyDaysAgo.toISOString());

      if (error) throw error;

      const groupedData = data.reduce((acc: Record<string, number>, item) => {
        const date = new Date(item.created_at).toISOString().split('T')[0];
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});

      const timelineData = [];
      for (let d = new Date(thirtyDaysAgo); d <= new Date(); d.setDate(d.getDate() + 1)) {
        const date = d.toISOString().split('T')[0];
        timelineData.push({
          date,
          count: groupedData[date] || 0
        });
      }

      return timelineData;
    }
  });

  const [isSending, setIsSending] = useState(false);

  const handleTriggerReport = async () => {
    setIsSending(true);
    try {
      const result = await sendManualChatbotReport();
      
      if (result.success) {
        toast({
          title: "Report Triggered",
          description: "SpudBot weekly report has been sent successfully",
        });
      } else {
        toast({
          title: "Report Error",
          description: "Failed to send SpudBot report. Check console for details.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error triggering report:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen py-24">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Admin Dashboard</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link to="/admin/membership-applications">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Membership Applications
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{membershipStats?.total || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {membershipStats?.pending || 0} pending applications
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/contact-submissions">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Contact Messages
                </CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{messageStats || 0}</div>
                <p className="text-xs text-muted-foreground">
                  Total messages received
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/articles/list">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Articles
                </CardTitle>
                <BarChart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{articleStats?.published || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {articleStats?.drafts || 0} drafts in progress
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 mb-8">
          <ApplicationsGraph data={applicationTimeline || []} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2">
              <Link 
                to="/admin/articles"
                className="flex items-center p-2 hover:bg-gray-100 rounded-lg"
              >
                <span className="ml-2">Create New Article</span>
              </Link>
              <Link 
                to="/admin/gallery"
                className="flex items-center p-2 hover:bg-gray-100 rounded-lg"
              >
                <span className="ml-2">Manage Gallery</span>
              </Link>
              <Button 
                variant="outline"
                onClick={handleTriggerReport}
                disabled={isSending}
                className="justify-start text-left"
              >
                <Bot className="mr-2 h-4 w-4" />
                {isSending ? "Sending Report..." : "Trigger SpudBot Weekly Report"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Upcoming feature: Activity log will show recent changes and updates.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
